// PolicyUpdateBanner.tsx — Phase 3.B
//
// Top-of-app banner shown to admins when one or more of the practice's binder
// policy documents have drifted to a newer template version and need re-
// acknowledgement. Mirrors BackfillBanner; mounted in AppShell. Self-hides when
// there are no pending updates (returns null).
//
// Tone: amber/warning — the practice stays fully operational (a pending policy
// update never blocks PHI, plan decision #9), so this nudges rather than blocks.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileWarning, ArrowRight } from 'lucide-react';
import { policyUpdateApi } from '../../lib/api';
import { useAuthStore } from '../../store';

interface PolicyUpdateStatusResponse {
  has_pending_updates: boolean;
  drifted: Array<{ template_key: string; display_name: string; current_version: string }>;
  in_progress: boolean;
}

export default function PolicyUpdateBanner() {
  const navigate = useNavigate();
  const { user, currentPractice } = useAuthStore();

  const roles = user?.roles ?? (user?.role ? [user.role] : []);
  const isAdmin =
    roles.includes('practice_admin') ||
    roles.includes('platform_admin') ||
    roles.includes('platform_operator');

  const statusQuery = useQuery<PolicyUpdateStatusResponse>({
    queryKey: ['policy-update-status', currentPractice?.practice_id],
    queryFn: async () => {
      const r = await policyUpdateApi.status();
      return r.data as PolicyUpdateStatusResponse;
    },
    enabled: isAdmin && !!currentPractice,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    retry: false,
  });

  const data = statusQuery.data;
  if (!data || !data.has_pending_updates) return null;

  const count = data.drifted.length;
  const docWord = count === 1 ? 'document' : 'documents';

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-3 text-sm">
      <FileWarning className="w-4 h-4 text-amber-700 flex-shrink-0" />
      <div className="flex-1 leading-snug text-amber-900">
        <strong className="font-semibold uppercase tracking-wide text-xs mr-2">
          Policy update
        </strong>
        <span className="font-medium">
          {count} HIPAA binder {docWord} {count === 1 ? 'has' : 'have'} been updated.
        </span>{' '}
        <span className="text-amber-800">
          Review what changed and re-acknowledge to keep your compliance record current.
        </span>
      </div>
      <button
        onClick={() => navigate('/policy-update')}
        className="flex items-center gap-1 text-xs font-semibold underline hover:no-underline whitespace-nowrap text-amber-900"
      >
        {data.in_progress ? 'Resume review' : 'Review updates'} <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
